import React, { useMemo } from "react";
import { Col, Container, Row } from "reactstrap";
import { useParams, useLocation } from "react-router-dom";
import { PageHeader } from "@/CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { useSystem } from "@/utils/hooks/useLocation";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useGetIncidentReportQuery, useIncidentReportLookupsQuery } from "@/services/reports";
import { Divider, Space } from "antd";
import dayjs from "dayjs";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { Info } from "@/Component/Info";

const emptyDisplay = "—";

const getLookupItems = (obj) =>
  obj?.items ?? obj?.data ?? (Array.isArray(obj) ? obj : []);

const formatLookupName = (item) =>
  item?.name_en ?? item?.name_ar ?? item?.code ?? emptyDisplay;

const findLookupLabel = (lookupList, id, getLocalizedText) => {
  if (id == null || id === "") return emptyDisplay;
  const items = getLookupItems(lookupList);
  const item = items.find((i) => String(i.id) === String(id));
  return item ? (getLocalizedText?.(item.name_ar, item.name_en) ?? item.name_en ?? item.name_ar ?? emptyDisplay) : String(id);
};

const LabelValue = ({ label, value }) => (
  <Col xs={12} md={6} lg={4} className="mb-3">
    <div className="text-secondary small fw-bold mb-1">{label}</div>
    <div className="text-dark">{value ?? emptyDisplay}</div>
  </Col>
);

const ViewReport = () => {
  const { t } = useTranslation();
  const { id: reportId } = useParams();
  const { state: locationState } = useLocation();
  const { currentSystem } = useSystem();
  const { getLocalizedText } = useLocalizedText();

  const fromBuildingsNotInspected = locationState?.fromBuildingsNotInspected === true;
  const headerTitle = fromBuildingsNotInspected ? t("VIEW_HOUSING_INSPECTION_REPORT") : t("VIEW_REPORT");
  const backTo = fromBuildingsNotInspected
    ? `/app/${currentSystem}/buildings-not-inspected`
    : `/app/${currentSystem}/reports`;

  const { data: reportResponse, isLoading } = useGetIncidentReportQuery(reportId, {
    system: currentSystem,
  });
  const { data: lookupsData } = useIncidentReportLookupsQuery({ system: currentSystem });

  const report = reportResponse?.data?.data ?? reportResponse?.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lookups = lookupsData?.data?.data ?? lookupsData?.data ?? {};

  const hasPilgrim = report?.pilgrim && typeof report.pilgrim === "object";
  const pilgrim = report?.pilgrim ?? {};

  const resolveLookup = useMemo(
    () => (key, id) => findLookupLabel(lookups[key], id, getLocalizedText),
    [lookups, getLocalizedText]
  );

  if (isLoading) {
    return (
      <div>
        <PageHeader title={headerTitle} backTo={backTo} sticky={false} />
        <Container fluid className="pb-5">
          <div className="text-center py-5 text-muted">{t("LOADING") || "Loading..."}</div>
        </Container>
      </div>
    );
  }

  if (!report) {
    return (
      <div>
        <PageHeader title={headerTitle} backTo={backTo} sticky={false} />
        <Container fluid className="pb-5">
          <div className="text-center py-5 text-muted">{t("REPORT_NOT_FOUND") || "Report not found."}</div>
        </Container>
      </div>
    );
  }

  const documents = report?.documents ?? [];
  const hasDocuments = Array.isArray(documents) && documents.length > 0;

  return (
    <div>
      <PageHeader
        title={headerTitle}
        backTo={backTo}
        sticky={false}
      />
      <Container fluid className="pb-5 pt-2">
        {/* Report metadata */}
        <Row className="g-3 mb-3">
          <Col xs={12} md={4} className="d-flex align-items-center">
            <span className="text-secondary me-2">{t("REPORT_NUMBER")}:</span>
            <span className="fw-bold">{report?.incident_number ?? report?.id ?? emptyDisplay}</span>
          </Col>
          <Col xs={12} md={4} className="d-flex align-items-center">
            <span className="text-secondary me-2">{t("CASE_DATE")}:</span>
            <span className="fw-bold">
              {report?.reported_at ? dayjs(report.reported_at).format("YYYY-MM-DD HH:mm") : emptyDisplay}
            </span>
          </Col>
          <Col xs={12} md={4} className="d-flex align-items-center">
            <span className="text-secondary me-2">{t("REPORT_CREATOR")}:</span>
            <span className="fw-bold">
              {getLocalizedText(report?.creator?.name_ar, report?.creator?.name_en) ?? emptyDisplay}
            </span>
          </Col>
          {/* resolved person */}
          <Col xs={12} md={4} className="d-flex align-items-center">
            <span className="text-secondary me-2">{t("RESOLVE_PERSON")}:</span>
            <span className="fw-bold">
              {getLocalizedText(report?.resolved_by?.name_ar, report?.resolved_by?.name_en) ?? emptyDisplay}
            </span>
          </Col>
          {/* report status */}
          <Col xs={12} md={4} className="d-flex align-items-center">
            <span className="text-secondary me-2">{t("REPORT_STATUS")}:</span>
            <span className={
              report?.resolved_at
                ? "badge badge-light-success"
                : "badge badge-light-warning"
            }>
              {report?.resolved_at ? t("RESOLVED") : t("UNRESOLVED")}
            </span>
          </Col>
        </Row>
        <Divider />

        {/* Person data: Pilgrim section OR Other section (based on report type) */}
        {hasPilgrim ? (
          <>
            <h5 className="mb-3 fw-bold">{t("PILGRIM_DATA")}</h5>
            <Row className="g-0 mb-4">
              <LabelValue
                label={t("PILGRIM_NUMBER")}
                value={pilgrim?.border_number ?? report?.pilgrim_id}
              />
              <LabelValue label={t("PASSPORT_NUMBER")} value={pilgrim?.passport_number} />
              <LabelValue
                label={t("PILGRIM_NAME")}
                value={getLocalizedText(pilgrim?.full_name_ar, pilgrim?.full_name_en) || report?.reported_person_name}
              />
              <LabelValue
                label={t("NATIONALITY")}
                value={
                  pilgrim?.nationality_id != null ? (
                    <SelectCountry.Display value={pilgrim.nationality_id} />
                  ) : (
                    emptyDisplay
                  )
                }
              />
              <LabelValue
                label={t("GENDER")}
                value={
                  pilgrim?.gender === "1" || pilgrim?.gender === "male"
                    ? t("MALE")
                    : pilgrim?.gender === "2" || pilgrim?.gender === "female"
                      ? t("FEMALE")
                      : emptyDisplay
                }
              />
              <LabelValue label={t("CAMPAIGN_OR_GROUP_NUMBER")} value={report?.campaign_number ?? pilgrim?.campaign_number} />
              <LabelValue
                label={t("SERVICE_CENTER")}
                value={
                  formatLookupName(report?.service_center ?? pilgrim?.service_center) ||
                  resolveLookup("service_centers", report?.service_center_id ?? pilgrim?.service_center_id)
                }
              />
              {currentSystem === "madina" && (
                <LabelValue
                  label={t("BAG_NUMBER")}
                  value={
                    report?.pilgrim?.bag_number ?? '-'
                  }
                />
              )}
              <Col xs={12} md={6} lg={4} className="mb-3">
                <div className="text-secondary small fw-bold mb-1">{t("HOUSING_CONTRACT")}</div>
                <div className="text-dark">
                  <SelectCachedOptions.Display
                    optionKey="makkahContracts"
                    campareBy="id"
                    value={report?.pilgrim?.contract_id }
                    render={(option) =>
                      option ? (
                        <Space>
                          <Info
                            title={`${t("GROUPS_FILTER_COMPANY")}: ${option.house_commercial_name_ar ?? "-"}`}
                          />
                          <span>{report?.pilgrim?.contract_id}</span>
                        </Space>
                      ) : (
                        <span>{emptyDisplay}</span>
                      )
                    }
                  />
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <h5 className="mb-3 fw-bold">{t("OTHER_ENTITIES_REPORT_TAB")}</h5>
            <Row className="g-0 mb-4">
              <LabelValue label={t("FULL_NAME")} value={report?.reported_person_name} />
              <LabelValue label={t("ID_NUMBER")} value={report?.reported_person_id_number} />
              <LabelValue label={t("MOBILE_NUMBER")} value={report?.reported_person_phone_number} />
            </Row>
          </>
        )}

        <Divider />

        {/* Case details */}
        <h5 className="mb-3 fw-bold">{t("CASE_DATA")}</h5>
        <Row className="g-0 mb-4">
          <LabelValue
            label={t("CASE_TYPES")}
            value={resolveLookup("case_types", report?.case_type_id ?? report?.case_type?.id)}
          />
          <LabelValue
            label={t("CASE_AUTHORITY")}
            value={resolveLookup("case_authorities", report?.case_authority_id ?? report?.case_authority?.id)}
          />
          <LabelValue
            label={t("CARD_STATUS")}
            value={resolveLookup("card_statuses", report?.card_status_id ?? report?.card_status?.id)}
          />
          <LabelValue
            label={t("REPORT_AUTHORITY")}
            value={resolveLookup("report_authorities", report?.report_authority_id ?? report?.report_authority?.id)}
          />
          <LabelValue
            label={t("CASE_DATE")}
            value={report?.reported_at ? dayjs(report.reported_at).format("YYYY-MM-DD HH:mm") : emptyDisplay}
          />
          <LabelValue
            label={t("DOCUMENT_TYPES")}
            value={resolveLookup("document_types", report?.document_type_id ?? report?.document_type?.id)}
          />
          <LabelValue label={t("DOCUMENT_NUMBER")} value={report?.document_number} />
          <LabelValue label={t("CASE_DESCRIPTION")} value={report?.description} />
          {report?.case_type?.code === "DEATH" && <LabelValue
            label={t("BODY_LOCATION")}
            value={getLocalizedText(report?.body_location?.name_ar, report?.body_location?.name_en)}
          />}
          {report?.resolved_at && (
            <LabelValue
              label={t("END_DATE")}
              value={dayjs(report.resolved_at).format("YYYY-MM-DD HH:mm")}
            />
          )}
          {/* resolved reason */}
          {report?.resolve_reason && (
            <LabelValue
              label={t("RESOLUTION_REASON")}
              value={report.resolve_reason}
            />
          )}

        </Row>

        {hasDocuments && (
          <>
            <Divider />
            <h5 className="mb-2 fw-bold">{t("ATTACHMENTS")}</h5>
            <ul className="list-unstyled">
              {documents.map((url, idx) => {
                const name = typeof url === "string" ? url.split("/").pop() || url.split("\\").pop() : t("ATTACHMENT");
                return (
                  <li key={idx} className="mb-1">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                      {name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Container>
    </div>
  );
};

export default ViewReport;
